import Head from 'next/head'
import Image from 'next/image'

import { Masthead, Root } from './blu-notte.style'
import Footer from './blu-notte/Footer'
import TAImage from './blu-notte/Image'
import ImageCarousel from './blu-notte/ImageCarousel'
import Slice from './blu-notte/Slice'
import Spacer from './blu-notte/Spacer'
import Text from './blu-notte/Text'
import caseAndMovementBg from './blu-notte/case-and-movement-bg.jpg'
import caseAndMovement from './blu-notte/case-and-movement.jpg'
import dialDiagram from './blu-notte/dial-diagram.jpg'
import galleryImage1 from './blu-notte/gallery/1.jpg'
import galleryImage2 from './blu-notte/gallery/2.jpg'
import galleryImage3 from './blu-notte/gallery/3.jpg'
import galleryImage4 from './blu-notte/gallery/4.jpg'
import galleryImage5 from './blu-notte/gallery/5.jpg'
import galleryImage6 from './blu-notte/gallery/6.jpg'
import haveFunBg from './blu-notte/have-fun-bg.jpg'
import haveFun from './blu-notte/have-fun.jpg'
import MastheadIdent from './blu-notte/masthead-ident.svg'
import mastheadRight from './blu-notte/masthead_right.jpg'
import unimatic from './blu-notte/unimatic.jpg'
import Unimatic from './blu-notte/unimatic.svg'
import watchface from './blu-notte/watchface.jpg'

const description = ''

const sliceImageSizes =
  '(max-width: 575px) calc(100vw - 6rem), (max-width: 768px) calc(100vw - 6rem), calc(50vw - 12rem)'

export default function Page() {
  return (
    <>
      <Head>
        <title>Blu Notte - The Armoury</title>
        <meta name="description" content={description} />
        {/* Facebook Meta Tags */}
        <meta property="og:url" content="https://www.thearmoury.com/watches/blu-notte" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blu Notte" />
        <meta property="og:description" content={description} />
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="watches.thearmoury.com" />
        <meta property="twitter:url" content="https://www.thearmoury.com/watches/blu-notte" />
        <meta name="twitter:title" content="Blu Notte" />
        <meta name="twitter:description" content={description} />

        <link rel="icon" type="image/png" href="/favicon-32x32.png" sizes="32x32" />
        <link rel="icon" type="image/png" href="/favicon-16x16.png" sizes="16x16" />
        <link rel="manifest" type="image/png" href="/manifest.json" />
        <link rel="mast-icon" href="/safari-pinned-tab.svg" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link rel="stylesheet" href="https://use.typekit.net/dbw1jpz.css" />
      </Head>

      <Root>
        <Masthead>
          <MastheadIdent className="w-full max-w-[492px] h-auto" />
          <Image src={mastheadRight} alt="" sizes="(max-width: 575px) 100vw, 50vw" />
        </Masthead>
        <Slice align="right" valign="center">
          <Unimatic className="w-full max-w-[308px] h-auto" />
          <Text header="Introduction">
            <p>
              The Modello Cinque U5S-TA ‘Blu Notte’ watch is a collaboration between Unimatic and The Armoury. This
              timepiece, the first collaboration between the two brands, merges Unimatic’s distinctive design with The
              Armoury’s timeless aesthetic.
            </p>
          </Text>
        </Slice>
        <TAImage src={dialDiagram} flush={true} />
        <Slice align="center">
          <Text header="Dial Design">
            <p>
              This daily wearer features our unique ‘Blu Notte’ midnight blue dial, with concentric circles pressed into 
              the dial surface reminiscent of rippling water. This type of visual treatment is often seen on subdials 
              but in the case of the 'Blu Notte', it is fully visualized across the face of the dial. The dial features 
              a cool gray open seconds rail and dial trims. The ladder hour and minutes hands, reverse lollipop second 
              hand with gray tips, float above the dial. The hour markers and hour and minute hands are filled with 
              Super-LumiNova® C1, making it exceptionally easy to read in low-light conditions.
            </p>
          </Text>
        </Slice>
        <TAImage src={watchface} flush={true} />
        <Slice bg={caseAndMovementBg.src}>
          <Text header="Case and Movement">
            <p>
              Housed inside the 36mm stainless steel case of the U5S-TA is the automatic caliber Sellita SW200-1b,
              28,800bph, with a power reserve of 41 hours and a hacking feature. On the back of the watch, the viewer is
              treated to a detailed engraving of concentric circles reflecting the dial’s design. The caseback also
              features the engraved phrase ‘Remove Jacket and Tie Before Diving’ for a touch of whimsy and is numbered X
              out of 50.
            </p>
          </Text>
          <Image src={caseAndMovement} alt="" sizes={sliceImageSizes} />
        </Slice>
        <Slice>
          <div>
            <Image src={unimatic} alt="" sizes={sliceImageSizes} />
          </div>
          <Text header="Unimatic">
            <p>
              Founded in 2015 by Giovanni Moro and Simone Nunziato, two Italian friends trained as product designers at
              Politecnico di Milano, UNIMATIC is an Italian contemporary watch brand. It blends the two friends’
              passions for vintage and contemporary design. Its goal is to offer unique edition watches that combine
              excellent design and high-quality standards at an affordable price. UNIMATIC believes in good design as a
              language and as a method. UNIMATIC’s design approach is based on function and utility. It extensively uses
              traditional references to reinterpret them in a clean and modern way, respectful of the user and of a
              timepiece’s iconic meaning.
            </p>
          </Text>
        </Slice>
        <Slice bg={haveFunBg.src}>
          <Text header="Straps & More">
            <p>
              The U5S-TA is fixed with a quick-release TPU strap in ‘Blu Notte’ and is shipped with an alternate nylon
              strap for added versatility. Each example is delivered in a branded UNIMATIC x The Armoury tough case with
              a warranty card and unique ID seal.
            </p>
          </Text>
          <Image src={haveFun} alt="" sizes={sliceImageSizes} />
        </Slice>
        <Slice>
          <Text header="Mark Cho’s Take">
            <p>
              “I’ve been a fan of Unimatic since their earliest releases,” said Mark Cho, co-founder of The Armoury.
              “Their watches have a thoughtful simplicity that I greatly admire, but I could not find one compact enough
              for my taste. Last year, Giovanni and Simone, founders of Unimatic, showed me the prototype for their 36mm
              Modello Cinque, and I was smitten. We proposed a collaboration immediately. We aimed to create a stylish,
              sturdy watch with a splash of colour and detail in the dial typical of The Armoury aesthetic. We are
              pleased to reveal our take on the Modello Cinque, the U5S-TA ‘Blu Notte.’”
            </p>
          </Text>
        </Slice>
        <ImageCarousel
          images={[galleryImage1, galleryImage2, galleryImage3, galleryImage4, galleryImage5, galleryImage6]}
        />
        <Spacer />
        <Footer />
      </Root>
    </>
  )
}

Page.getLayout = function getLayout(page) {
  return <div className="overflow-x-hidden">{page}</div>
}
